// Marketplace-specific SEO landing page data. Each entry is fully unique — not
// a templated swap. Slugs match /services/{slug}/ routes and sitemap.xml.

export const MARKETPLACE_PAGES = {
  "amazon-seller-account-management": {
    marketplace: "Amazon India",
    hero: "Amazon Seller Account Management Services in India",
    intent: "amazon seller account management",
    tagline: "End-to-end Amazon India seller management — from Seller Central setup to Buy Box, FBA and A+ content.",
    intro: "Orina Enterprise runs full-service Amazon seller account management for Indian D2C brands. We manage the daily operations, listings, ads, account health and growth strategy on Amazon Seller Central so your team can focus on product and brand.",
    process: [
      { t: "Amazon account audit", d: "Complete review of Seller Central, listings, ads, IPI, account health, ODR, late shipment and voice-of-customer signals." },
      { t: "Brand Registry & category approval", d: "Enrol Brand Registry, unlock A+ content, apply for gated categories (Beauty, Grocery, Wellness, etc.)." },
      { t: "Listing optimization", d: "Title, bullets, backend keywords, images and A+ modules rewritten for Amazon India search algorithm." },
      { t: "Ads management", d: "Sponsored Products, Sponsored Brands, Sponsored Display and DSP — managed for TACoS not vanity ACoS." },
      { t: "FBA / MFN operations", d: "Inventory planning, IPI improvement, FBA shipments, storage-fee optimisation." },
      { t: "Account health", d: "ODR monitoring, IP complaints, policy violations, appeals and reinstatements." },
      { t: "Weekly reporting", d: "P&L per SKU, ROAS, TACoS, review pace, competitor gaps." }
    ],
    outcomes: [
      "Buy Box eligibility on hero SKUs",
      "A+ content and Brand Store live",
      "Managed ad spend with weekly ROAS reports",
      "Account health kept within Amazon's thresholds"
    ],
    faqs: [
      { q: "Can you help with Amazon account suspensions?", a: "Yes. We handle policy violation appeals, IP complaint responses and full account reinstatement." },
      { q: "Do you manage Amazon Ads separately or bundled?", a: "Bundled inside our monthly retainer. We do not charge a % of ad spend on top." },
      { q: "Which categories do you support on Amazon India?", a: "Beauty, F&B, Home, Kitchen, Wellness, Fashion, Baby, Sports, Electronics and Grocery." },
      { q: "How long before I see results?", a: "Listing changes: 7-21 days. Ads: 30 days to stabilise. Account health: immediate." }
    ]
  },
  "flipkart-seller-account-management": {
    marketplace: "Flipkart",
    hero: "Flipkart Seller Account Management Services in India",
    intent: "flipkart seller account management",
    tagline: "F-Assured onboarding, PLA advertising, returns control and account health across Flipkart Seller Hub.",
    intro: "Orina Enterprise runs day-to-day Flipkart seller management for Indian brands — F-Assured qualification, listing optimisation for Flipkart's description-heavy algorithm, PLA / PCA / Brand Stories advertising, and returns control.",
    process: [
      { t: "Seller Hub audit", d: "Cross-check pricing, F-Assured eligibility, catalog completeness, Q&A backlog and account health." },
      { t: "Listing rewrites", d: "Title, description and rich content optimised for Flipkart's description-weighted search algorithm." },
      { t: "F-Assured qualification", d: "Ship 95%+ on SLA, keep cancellations under 1%, control returns to earn and retain the badge." },
      { t: "Ads: PLA + PCA + Brand Stories", d: "Keyword and category targeting; Brand Stories for Brand Store owners." },
      { t: "Returns management", d: "Quality Check at pack-out, verified-review programs, pincode-level fraud control." },
      { t: "Big Billion Days prep", d: "Deal submission, inventory planning, war-room execution, post-event analysis." }
    ],
    outcomes: [
      "F-Assured badge live on hero SKUs",
      "Return rate reduced to platform-competitive levels",
      "Managed PLA campaigns with weekly ROAS reports",
      "Big Billion Days participation"
    ],
    faqs: [
      { q: "Do you help earn the F-Assured badge?", a: "Yes. We run the full workflow — logistics setup, SLA discipline, cancellation control, return management." },
      { q: "Which Flipkart ad products do you manage?", a: "PLA (Product Listing Ads), PCA (Product Category Ads), Flipkart Brand Stories and Deal placements." },
      { q: "Can you reduce Flipkart returns?", a: "Yes. Our QC + verified-review + pincode-fraud playbook typically brings returns down significantly over 60-90 days." },
      { q: "Do you handle Flipkart account suspensions?", a: "Yes — including performance suspensions, IP complaints and cancellation-rate escalations." }
    ]
  },
  "meesho-seller-account-management": {
    marketplace: "Meesho",
    hero: "Meesho Seller Account Management Services in India",
    intent: "meesho seller account management",
    tagline: "Catalog depth, reseller-friendly creatives and Meesho Ads — built for margin discipline.",
    intro: "Meesho is India's largest tier-2/3 marketplace. Orina Enterprise runs full-service Meesho seller management — catalog planning, reseller-optimised imagery, returns control and Meesho Ads — with rigorous margin discipline.",
    process: [
      { t: "Catalog depth planning", d: "Meesho rewards breadth: we plan a 50-500 SKU catalog roadmap for your category." },
      { t: "Reseller-optimised creatives", d: "Imagery designed to reshare on WhatsApp, Instagram, Facebook — Meesho's real distribution channel." },
      { t: "Meesho Ads management", d: "Keyword-targeted campaigns on hero SKUs at margin-preserving CPCs." },
      { t: "Returns control", d: "QC at packing, pincode fraud blacklists, COD blacklist for repeat returners." },
      { t: "Pricing engineering", d: "All-in pricing (SP + zero shipping + reseller markup) modelled per SKU." },
      { t: "Weekly reporting", d: "SKU velocity, return rate, ROAS, catalog gaps." }
    ],
    outcomes: [
      "50+ SKU catalog live",
      "Return rate contained at category baseline",
      "Ad-driven SKU velocity",
      "Predictable margin per SKU"
    ],
    faqs: [
      { q: "How many SKUs should I launch on Meesho?", a: "Minimum 50 to activate the algorithm. Hero catalogs typically run 200-500." },
      { q: "How do you control Meesho returns?", a: "QC at packing + pincode-level fraud filtering + verified-review programs." },
      { q: "Is Meesho profitable?", a: "For the right SKU price and pack size, yes. We model unit economics before onboarding." },
      { q: "Do you help with Meesho Ads?", a: "Yes — including keyword research, campaign structure and daily bid optimisation." }
    ]
  },
  "jiomart-seller-onboarding": {
    marketplace: "JioMart",
    hero: "JioMart Seller Onboarding & Account Management",
    intent: "jiomart seller onboarding",
    tagline: "JioMart go-live in 14 days — catalog, category codes, fulfilment and JioMart Ads.",
    intro: "JioMart is Reliance Retail's digital marketplace, with 18,000+ retail stores acting as fulfilment nodes. Orina Enterprise onboards Indian brands to JioMart's partner portal, uploads compliant catalogs and manages daily operations.",
    process: [
      { t: "KYC & documentation", d: "GST, PAN, bank proof, brand authorization, trademark. JioMart approval within 5-10 days." },
      { t: "Catalog compliance", d: "Correct category codes, HSN codes, image dimensions, MRP-SP-GST reconciliation." },
      { t: "Fulfilment setup", d: "JioMart Fulfilled vs Seller Fulfilled — chosen per SKU velocity." },
      { t: "Pricing & multi-pack strategy", d: "MRP discipline, multi-pack pricing for tier-2/3 audience." },
      { t: "JioMart Ads", d: "Sponsored Products at 30-50% lower CPCs than Amazon (early-mover advantage)." },
      { t: "Festive prep", d: "Republic Day, Independence Day and JioMart Festive Days participation." }
    ],
    outcomes: [
      "Live on JioMart in 14 days",
      "Catalog approved without HSN/category rejections",
      "Managed sponsored campaigns",
      "Multi-pack SKUs for tier-2/3 audience"
    ],
    faqs: [
      { q: "How long does JioMart onboarding take?", a: "KYC + catalog approval typically 14 days. Complex categories can take 21 days." },
      { q: "Is JioMart profitable for D2C brands?", a: "Yes for FMCG, home, grocery and personal care. Fashion and premium beauty perform less well." },
      { q: "Do you manage JioMart Ads?", a: "Yes — including keyword research and daily bid management." },
      { q: "Which fulfillment mode is better?", a: "JioMart Fulfilled for hero SKUs; Seller Fulfilled for long-tail. We model per SKU." }
    ]
  },
  "myntra-seller-onboarding": {
    marketplace: "Myntra",
    hero: "Myntra Seller Onboarding & Fashion Marketplace Management",
    intent: "myntra seller onboarding",
    tagline: "Myntra brand approval, fashion catalog production and End of Reason Sale execution.",
    intro: "Myntra is India's #1 fashion destination. Onboarding requires brand approval and fashion-specific catalog production (model shots, size charts, shade grids). Orina Enterprise handles the entire lifecycle for fashion, lifestyle and accessories brands.",
    process: [
      { t: "Brand approval application", d: "Myntra requires brand pitch, catalog samples, financial commitment. We prepare the full package." },
      { t: "Fashion catalog production", d: "Model shoots, mannequin shots, size charts, fabric close-ups." },
      { t: "Listing optimisation", d: "Fashion titles, occasion tags, styling notes, care instructions." },
      { t: "Myntra Ads & M-Insider integration", d: "Sponsored placements, deal placements, M-Insider loyalty program integration." },
      { t: "End of Reason Sale prep", d: "Deal submission, inventory planning, war-room execution." },
      { t: "Returns discipline", d: "Verified reviews, size accuracy and QC at packing to reduce regret returns." }
    ],
    outcomes: [
      "Myntra brand approval",
      "Fashion catalog live with 30+ SKUs",
      "Managed Myntra Ads with weekly reports",
      "End of Reason Sale participation"
    ],
    faqs: [
      { q: "How long does Myntra brand approval take?", a: "3-6 weeks for approved brands. Rejected brands can re-apply after 90 days with improvements." },
      { q: "Do I need fashion photography?", a: "Yes — Myntra requires model shots, mannequin shots, care/fabric close-ups." },
      { q: "Which fashion categories perform best on Myntra?", a: "Ethnic, casual women's wear, activewear, footwear and accessories." },
      { q: "Do you handle End of Reason Sale?", a: "Yes — deal submissions, inventory prep, war-room execution and post-sale analysis." }
    ]
  },
  "ajio-seller-onboarding": {
    marketplace: "AJIO",
    hero: "AJIO Seller Onboarding — Fashion & Lifestyle Brands",
    intent: "ajio seller onboarding",
    tagline: "AJIO brand approval, curated catalog production and AJIO Luxe onboarding for premium brands.",
    intro: "AJIO is Reliance's fashion and lifestyle marketplace, with AJIO Luxe serving the premium and luxury segment. Orina Enterprise handles brand approval, catalog production and account management for AJIO and AJIO Luxe.",
    process: [
      { t: "Brand approval & AJIO Luxe eligibility", d: "AJIO reviews brand identity, pricing tier and category fit. AJIO Luxe needs premium positioning." },
      { t: "Curated catalog production", d: "Studio and lifestyle shoots, brand story, fabric and finish close-ups." },
      { t: "Listing optimisation", d: "Fashion titles, size charts, styling occasions, care." },
      { t: "AJIO Ads & placements", d: "Sponsored placements, homepage banners for premium brands." },
      { t: "Sale event participation", d: "AJIO Freedom Sale, End of Reason equivalent, Republic Day Fashion Sale." },
      { t: "Returns discipline", d: "Verified reviews, size accuracy, QC at packing." }
    ],
    outcomes: [
      "AJIO or AJIO Luxe brand approval",
      "Curated catalog live",
      "Sale event participation",
      "Managed Sponsored placements"
    ],
    faqs: [
      { q: "How long does AJIO brand approval take?", a: "3-5 weeks for standard AJIO. 4-8 weeks for AJIO Luxe." },
      { q: "What is the difference between AJIO and AJIO Luxe?", a: "AJIO covers mid-market fashion; AJIO Luxe is premium and luxury with tighter brand curation." },
      { q: "Do you produce the fashion catalog?", a: "Yes — through partner studios in Mumbai and Delhi." },
      { q: "What are typical AJIO commissions?", a: "Category-dependent, typically 15-30%. Confirm exact rates during onboarding." }
    ]
  },
  "nykaa-seller-onboarding": {
    marketplace: "Nykaa",
    hero: "Nykaa Seller Onboarding — Beauty, Wellness & Personal Care",
    intent: "nykaa seller onboarding",
    tagline: "Nykaa brand approval, beauty catalog production and Pink Friday sale participation.",
    intro: "Nykaa is India's #1 beauty and personal care marketplace. Brand approval is competitive and catalog quality standards are high. Orina Enterprise runs the full onboarding and account management workflow for beauty, wellness and personal care brands.",
    process: [
      { t: "Nykaa brand approval", d: "Brand pitch, product samples, category fit review. Approval typically 4-8 weeks." },
      { t: "Beauty catalog production", d: "Shade grids, skin-tone diversity, ingredient close-ups, before/after imagery." },
      { t: "Brand Page setup", d: "Nykaa Brand Page banners and hero SKU curation." },
      { t: "Listing optimisation", d: "Beauty titles, skin-type tags, concern tags, usage instructions." },
      { t: "Nykaa Ads & placements", d: "Sponsored placements, category banners, homepage banners for approved brands." },
      { t: "Pink Friday & festive prep", d: "Deal submission, inventory planning, review push, war-room execution." }
    ],
    outcomes: [
      "Nykaa brand approval",
      "Beauty catalog live with shade grids and ingredient close-ups",
      "Brand Page live",
      "Pink Friday sale participation"
    ],
    faqs: [
      { q: "How long does Nykaa brand approval take?", a: "4-8 weeks. Faster if you have a clean brand story, strong hero SKUs and existing reviews from other platforms." },
      { q: "Do you produce beauty catalog imagery?", a: "Yes — shade grids, model shots, ingredient close-ups, before/after imagery." },
      { q: "Which beauty categories perform best on Nykaa?", a: "Skincare, haircare, makeup and personal care. Wellness and body care are growing fast." },
      { q: "What is Nykaa's commission structure?", a: "Category-dependent, typically 20-35%. Confirm exact rates during onboarding." }
    ]
  },
  "tatacliq-seller-onboarding": {
    marketplace: "Tata CLiQ",
    hero: "Tata CLiQ Seller Onboarding & Authentic Brand Management",
    intent: "tata cliq seller onboarding",
    tagline: "Tata CLiQ brand approval, authenticated catalog production and Tata CLiQ Luxury enrollment.",
    intro: "Tata CLiQ is the Tata Group's premium marketplace, with Tata CLiQ Luxury covering the true luxury segment. All listings are authenticated. Orina Enterprise handles brand approval, catalog production and daily management.",
    process: [
      { t: "Brand approval & authentication", d: "Tata CLiQ verifies brand authenticity. Prepare trademark, distributor agreements, product samples." },
      { t: "Tata CLiQ Luxury eligibility", d: "Premium and luxury brands can apply. Approval is competitive." },
      { t: "Catalog production", d: "Premium-quality studio and lifestyle imagery." },
      { t: "Listing optimisation", d: "Premium titles, occasion tags, authenticated badges." },
      { t: "Sponsored placements", d: "Sponsored SKUs, homepage banners for premium brands." },
      { t: "Festive prep", d: "Tata CLiQ Great Festive Sale, Republic Day Sale participation." }
    ],
    outcomes: [
      "Tata CLiQ (or Tata CLiQ Luxury) brand approval",
      "Authenticated catalog live",
      "Managed Sponsored placements",
      "Festive sale participation"
    ],
    faqs: [
      { q: "How long does Tata CLiQ brand approval take?", a: "4-6 weeks for standard Tata CLiQ; 6-10 weeks for Tata CLiQ Luxury." },
      { q: "Are all listings authenticated?", a: "Yes — Tata CLiQ requires proof of authenticity for every SKU." },
      { q: "Which categories perform best?", a: "Premium fashion, watches, jewellery, beauty, home décor." },
      { q: "What is Tata CLiQ's commission structure?", a: "Category-dependent, typically 15-30%. Confirm exact rates during onboarding." }
    ]
  },
  "blinkit-seller-onboarding": {
    marketplace: "Blinkit",
    hero: "Blinkit Seller Onboarding — Quick Commerce Growth Partner",
    intent: "blinkit seller onboarding",
    tagline: "Blinkit onboarding, dark-store SKU planning and Blinkit Ads management.",
    intro: "Blinkit (part of Eternal, formerly Zomato) is India's largest quick-commerce network with the deepest dark-store footprint in metros. Orina Enterprise onboards D2C brands with a hero-SKU-first strategy, city-wise demand planning and Blinkit Ads management.",
    process: [
      { t: "Brand pitch & onboarding", d: "Brand deck, sample products, distributor agreement. Blinkit KAM alignment." },
      { t: "Hero SKU planning", d: "5-10 pack-sizes under ₹200 that fit impulse-buy behaviour." },
      { t: "Dark-store demand planning", d: "City-wise stock allocation. Monitor stockout by dark-store." },
      { t: "Blinkit Ads management", d: "Sponsored placements on hero SKUs. Impression share targeting." },
      { t: "Reviews & ratings push", d: "Day-1 rating discipline. Response to every review for 90 days." },
      { t: "City-wise expansion", d: "Start in 2-3 cities. Expand based on sell-through data." }
    ],
    outcomes: [
      "5-10 hero SKUs live on Blinkit",
      "City-wise stock allocation optimised",
      "Managed Blinkit Ads with weekly reports",
      "Sell-through rate tracked per dark-store"
    ],
    faqs: [
      { q: "Which SKUs work on Blinkit?", a: "Pack-sizes under ₹200 with high-velocity impulse-purchase behaviour. Grocery, snacks, personal care, beverages." },
      { q: "How long does Blinkit onboarding take?", a: "Typically 18-25 days for approved brands. Category-dependent." },
      { q: "Is Blinkit profitable?", a: "Yes for the right SKU price and margin structure. We model unit economics before onboarding." },
      { q: "Do you manage Blinkit Ads?", a: "Yes — including keyword research and daily bid management." }
    ]
  },
  "zepto-seller-onboarding": {
    marketplace: "Zepto",
    hero: "Zepto Seller Onboarding — Quick Commerce & Tier-2 Expansion",
    intent: "zepto seller onboarding",
    tagline: "Zepto onboarding, hyperlocal SKU planning and Zepto Ads management.",
    intro: "Zepto is India's fastest-growing quick-commerce network with aggressive expansion into tier-2 and tier-3 cities. Orina Enterprise onboards D2C brands with a tight hero-SKU strategy, hyperlocal demand planning and Zepto Ads management.",
    process: [
      { t: "Brand pitch & KAM alignment", d: "Brand deck, sample products, distributor agreement." },
      { t: "Hero SKU strategy", d: "5-10 hero SKUs at impulse-buy price points." },
      { t: "Hyperlocal demand planning", d: "City-wise, dark-store-wise stock allocation." },
      { t: "Zepto Ads management", d: "Sponsored placements on hero SKUs. Impression share targeting." },
      { t: "Reviews & ratings discipline", d: "Day-1 rating discipline. Response to every review." },
      { t: "Tier-2/3 expansion planning", d: "Start in top-5 metros. Expand based on sell-through." }
    ],
    outcomes: [
      "5-10 hero SKUs live on Zepto",
      "Dark-store-level stock allocation",
      "Managed Zepto Ads with weekly reports",
      "Tier-2/3 expansion plan"
    ],
    faqs: [
      { q: "How is Zepto different from Blinkit?", a: "Zepto is more aggressive in tier-2/3 expansion. Slightly lower commission on many categories." },
      { q: "Which SKUs work best on Zepto?", a: "Impulse-purchase SKUs under ₹300 with high velocity. Snacks, personal care, home essentials." },
      { q: "How long does Zepto onboarding take?", a: "Typically 20-30 days for approved brands." },
      { q: "Do you manage Zepto Ads?", a: "Yes — including keyword research and daily bid management." }
    ]
  },
  "instamart-seller-onboarding": {
    marketplace: "Instamart",
    hero: "Instamart Seller Onboarding — Swiggy's Quick Commerce Network",
    intent: "instamart seller onboarding",
    tagline: "Instamart onboarding, food-adjacent SKU planning and cross-Swiggy amplification.",
    intro: "Instamart is Swiggy's quick-commerce arm. Its unique advantage: existing Swiggy food-delivery customers cross-shop groceries and essentials. Orina Enterprise onboards D2C brands with a food-adjacent SKU strategy and Instamart Ads management.",
    process: [
      { t: "Brand pitch & KAM alignment", d: "Brand deck, samples, distributor agreement." },
      { t: "Food-adjacent SKU strategy", d: "SKUs that pair well with Swiggy food-order behaviour — snacks, beverages, condiments, personal care." },
      { t: "City-wise demand planning", d: "Instamart's dark stores follow Swiggy's density map. Plan accordingly." },
      { t: "Instamart Ads management", d: "Sponsored placements, cross-Swiggy amplification." },
      { t: "Reviews discipline", d: "Day-1 rating discipline." },
      { t: "Cross-Swiggy promotions", d: "Coupon stacking with Swiggy food orders when applicable." }
    ],
    outcomes: [
      "Hero SKUs live on Instamart",
      "Food-adjacent SKU strategy operational",
      "Managed Instamart Ads",
      "Cross-Swiggy amplification if enabled"
    ],
    faqs: [
      { q: "Which SKUs work on Instamart?", a: "Snacks, beverages, condiments, personal care, home essentials. Anything that pairs with a Swiggy food order." },
      { q: "How is Instamart different from Blinkit and Zepto?", a: "Instamart has Swiggy's existing customer base. Cross-sell to food-delivery customers is a unique advantage." },
      { q: "How long does Instamart onboarding take?", a: "Typically 20-30 days for approved brands." },
      { q: "Do you manage Instamart Ads?", a: "Yes — including keyword research and daily bid management." }
    ]
  }
};

export const MARKETPLACE_SLUGS = Object.keys(MARKETPLACE_PAGES);
